import { CheckCircle2, Globe2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Motion";
import { Seo } from "@/components/Seo";
import { whatsappWithText } from "@/lib/site";
import { WhatsAppIcon } from "@/components/WhatsAppButton";

const REGIONS = [
  {
    id: "india",
    eyebrow: "India",
    title: "Indian universities",
    text: "We regularly assist students enrolled in distance-learning, executive, and full-time programmes across India's leading universities. Our writers know the project report structures, certificate pages, and evaluation conventions these institutions expect.",
    image: "https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=900&q=60",
    imageAlt: "University campus building in India",
    universities: [
      "IGNOU (Indira Gandhi National Open University)",
      "Symbiosis (SCDL & SIU programmes)",
      "UPES, Dehradun",
      "DY Patil University",
      "NMIMS Global Access",
      "Amity University (Online & On-campus)",
      "Sikkim Manipal University",
      "Annamalai University",
      "University of Mumbai",
      "Savitribai Phule Pune University",
      "University of Delhi",
      "Anna University",
    ],
  },
  {
    id: "europe",
    eyebrow: "Europe & United Kingdom",
    title: "European universities",
    text: "From UK-style dissertations to Bologna-framework theses across the continent, we write to the argument-led, critically engaged standards European examiners reward — with Harvard, APA, OSCOLA, and Vancouver referencing executed precisely.",
    image: "https://images.pexels.com/photos/31656148/pexels-photo-31656148.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    imageAlt: "Historic European university architecture",
    universities: [
      "Heinrich Heine University Düsseldorf",
      "London Business School",
      "University of London (incl. distance programmes)",
      "University of Manchester",
      "Oxford Brookes University",
      "Coventry University",
      "Technical University of Munich",
      "University of Amsterdam",
      "HEC Paris",
      "Bocconi University, Milan",
      "Trinity College Dublin",
      "ESADE Business School, Barcelona",
    ],
  },
  {
    id: "us-canada",
    eyebrow: "United States & Canada",
    title: "US & Canadian universities",
    text: "North American programmes emphasise continuous assessment — term papers, case analyses, and capstone projects alongside theses. We support students with APA/MLA-compliant writing, literature reviews, and research projects built to rubric.",
    image: "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=900&q=60",
    imageAlt: "North American university building",
    universities: [
      "Arizona State University",
      "New York University (NYU)",
      "Northeastern University",
      "University of the People",
      "University of Toronto",
      "York University, Toronto",
      "Athabasca University",
      "Thompson Rivers University",
      "Concordia University, Montreal",
    ],
  },
  {
    id: "middle-east",
    eyebrow: "Middle East",
    title: "Middle East universities",
    text: "A growing share of our students study at international branch campuses and regional universities across the Gulf. We understand the blended UK/US academic conventions these institutions follow and the working-professional schedules of their students.",
    image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=900&q=60",
    imageAlt: "Dubai city skyline",
    universities: [
      "Heriot-Watt University Dubai",
      "University of Wollongong in Dubai",
      "BITS Pilani, Dubai Campus",
      "Middlesex University Dubai",
      "American University of Sharjah",
      "Qatar University",
      "Manipal Academy of Higher Education, Dubai",
    ],
  },
];

export default function UniversitiesHub() {
  return (
    <div data-testid="universities-page">
      <Seo
        title="Universities We Serve — India, Europe, US, Canada & Middle East | MBA Project & Thesis Writing Help"
        description="Academic writing support for students at IGNOU, Symbiosis, UPES, DY Patil, London Business School, Heinrich Heine Düsseldorf, and universities across the US, Canada, and Middle East."
      />
      <PageHero
        eyebrow="Universities we serve"
        lines={["One team.", "Four continents", "of academic standards."]}
        sub="Wherever you study, we write to your institution's exact format and evaluation conventions. Below are universities whose students we regularly assist."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 space-y-20 md:space-y-28">
        {REGIONS.map((r, idx) => (
          <Reveal key={r.id}>
            <div
              data-testid={`region-${r.id}`}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-start ${idx % 2 === 1 ? "" : ""}`}
            >
              <div className={`lg:col-span-5 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="overflow-hidden rounded-2xl border border-slate-200 group">
                  <img
                    src={r.image}
                    alt={r.imageAlt}
                    className="w-full h-64 md:h-80 object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>
              <div className={`lg:col-span-7 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                <Eyebrow>{r.eyebrow}</Eyebrow>
                <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">{r.title}</h2>
                <p className="mt-4 text-sm md:text-base text-slate-600 leading-relaxed">{r.text}</p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {r.universities.map((u) => (
                    <li key={u} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                      <CheckCircle2 className="h-4 w-4 text-[#2563EB] mt-0.5 shrink-0" strokeWidth={1.5} />
                      {u}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        ))}

        <Reveal className="border border-slate-200 rounded-2xl bg-[#F5F8FF] p-8 md:p-10">
          <div className="flex items-start gap-4">
            <Globe2 className="h-6 w-6 text-[#2563EB] mt-1 shrink-0" strokeWidth={1.5} />
            <div>
              <h2 className="font-display font-bold text-xl tracking-tight">Don't see your university?</h2>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-2xl">
                These lists are only a sample. Share your institution's guideline document on WhatsApp and we'll confirm exactly how we'd structure and format your submission — before you commit to anything.
              </p>
              <p className="mt-4 text-xs text-slate-400 leading-relaxed max-w-2xl">
                University names are referenced solely to indicate the academic formats our writers are familiar with. We are an independent academic support service and are not affiliated with, endorsed by, or connected to any institution listed.
              </p>
              <a
                href={whatsappWithText("Hi, I study at a university not listed on your site. Can you help with my format?")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="universities-whatsapp-cta"
                className="mt-6 inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5"
              >
                <WhatsAppIcon className="h-5 w-5" /> Share Your Guidelines
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
